import React, { useRef } from 'react';
import { FaPrint, FaTimes } from 'react-icons/fa';

export default function AnalyticsModal({ open, onClose, reportContent, chartImages }) {
  const printRef = useRef();

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=1000,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Restaurant Analytics Report</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 32px; background: #f8fafc; color: #22223b; }
            .modal-content { background: #fff; border-radius: 18px; box-shadow: 0 8px 32px rgba(0,0,0,0.12); padding: 32px; max-width: 900px; margin: 0 auto; }
            h1, h2, h3, h4 { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; }
            h1 { font-size: 2.2rem; font-weight: 700; margin-bottom: 8px; }
            h2 { font-size: 1.4rem; font-weight: 600; margin: 24px 0 12px 0; }
            h3 { font-size: 1.1rem; font-weight: 600; margin: 16px 0 8px 0; }
            .section { margin-bottom: 32px; }
            .section-title { font-size: 1.2rem; font-weight: 600; margin-bottom: 12px; color: #4f46e5; letter-spacing: 0.5px; }
            .metrics-table, .data-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
            .metrics-table th, .metrics-table td, .data-table th, .data-table td { border: 1px solid #e5e7eb; padding: 10px 14px; text-align: left; font-size: 1rem; }
            .metrics-table th, .data-table th { background: #f1f5f9; font-weight: 600; }
            .metrics-table td { font-weight: 500; }
            .metrics-table .change { font-size: 0.95em; color: #059669; font-weight: 400; }
            .date-range { color: #64748b; font-size: 1rem; margin-bottom: 18px; }
            .charts-grid { display: flex; gap: 24px; margin: 24px 0; }
            .chart-container { flex: 1; background: #f8fafc; border-radius: 12px; padding: 18px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); display: flex; flex-direction: column; align-items: center; }
            .chart-title { font-size: 1rem; font-weight: 600; margin-bottom: 10px; color: #6366f1; }
            .chart-image { width: 100%; max-width: 340px; height: auto; border-radius: 8px; background: #fff; }
            @media (max-width: 900px) {
              .charts-grid { flex-direction: column; gap: 16px; }
              .chart-image { max-width: 100%; }
            }
            @media print {
              body { -webkit-print-color-adjust: exact; background: #fff; }
              .modal-content { box-shadow: none; padding: 0; }
              .section { page-break-inside: avoid; }
              .chart-container { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="modal-content">${printRef.current.innerHTML}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  if (!open) return null;

  // Extract and reformat reportContent for tabular layout
  // We'll assume reportContent is a React element with the correct structure
  // For charts, use a grid layout

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative p-8" ref={printRef}>
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl"
          onClick={onClose}
          title="Close"
        >
          <FaTimes />
        </button>
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800">Analytics Report Preview</h2>
          <button
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-2 rounded-lg shadow flex items-center gap-2 hover:from-indigo-700 hover:to-purple-700 text-lg"
            onClick={handlePrint}
          >
            <FaPrint /> Print
          </button>
        </div>
        {/* Report Content - force tabular layout */}
        <div>
          {/* Header and Date Range */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold mb-1">Restaurant Analytics Report</h1>
            <div className="date-range mb-2">{reportContent.props.children[0].props.children[1]}</div>
          </div>
          {/* Key Metrics Table */}
          <div className="section">
            <div className="section-title">Key Performance Metrics</div>
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Value</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Revenue</td>
                  <td>{reportContent.props.children[1].props.children[1].props.children[0].props.children[1].props.children}</td>
                  <td className="change">+12.5% from previous period</td>
                </tr>
                <tr>
                  <td>Total Orders</td>
                  <td>{reportContent.props.children[1].props.children[1].props.children[1].props.children[1].props.children}</td>
                  <td className="change">+8.2% from previous period</td>
                </tr>
                <tr>
                  <td>Average Order Value</td>
                  <td>{reportContent.props.children[1].props.children[1].props.children[2].props.children[1].props.children}</td>
                  <td className="change">+4.1% from previous period</td>
                </tr>
                <tr>
                  <td>Unique Customers</td>
                  <td>{reportContent.props.children[1].props.children[1].props.children[3].props.children[1].props.children}</td>
                  <td className="change">+15.3% from previous period</td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Top Selling Items Table */}
          <div className="section">
            <div className="section-title">Top Selling Items</div>
            {reportContent.props.children[2].props.children[1]}
          </div>
          {/* Daily Revenue Breakdown Table */}
          <div className="section">
            <div className="section-title">Daily Revenue Breakdown</div>
            {reportContent.props.children[3].props.children[1]}
          </div>
          {/* Order Status Summary Table */}
          <div className="section">
            <div className="section-title">Order Status Summary</div>
            {reportContent.props.children[4].props.children[1]}
          </div>
          {/* Recent Orders Table */}
          <div className="section">
            <div className="section-title">Recent Orders</div>
            {reportContent.props.children[5].props.children[1]}
          </div>
        </div>
        {/* Charts Side by Side */}
        {chartImages && (
          <div className="charts-grid mt-8">
            {chartImages.revenue && (
              <div className="chart-container">
                <div className="chart-title">Revenue Trend Analysis</div>
                <img src={chartImages.revenue} alt="Revenue Trend" className="chart-image" />
              </div>
            )}
            {chartImages.topItems && (
              <div className="chart-container">
                <div className="chart-title">Top Selling Items Distribution</div>
                <img src={chartImages.topItems} alt="Top Selling Items" className="chart-image" />
              </div>
            )}
            {chartImages.orderVolume && (
              <div className="chart-container">
                <div className="chart-title">Daily Order Volume</div>
                <img src={chartImages.orderVolume} alt="Order Volume" className="chart-image" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 